import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { translations, LANGUAGES } from "@/i18n/translations";

const LanguageContext = createContext(null);
const SUPPORTED = LANGUAGES.map((l) => l.code);

function lookup(obj, path) {
  return path.split(".").reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj);
}

function detectInitialLang() {
  try {
    const saved = localStorage.getItem("uvg_lang");
    if (SUPPORTED.includes(saved)) return saved;
  } catch (e) { console.warn("Language preference storage unavailable:", e); }
  try {
    const nav = ((navigator.languages && navigator.languages[0]) || navigator.language || "en")
      .slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(nav)) return nav;
  } catch (e) { console.warn("Browser language detection unavailable:", e); }
  return "en";
}

function persistLang(l) {
  try {
    document.documentElement.lang = l;
    localStorage.setItem("uvg_lang", l);
  } catch (e) {
    console.warn("Could not persist language preference (storage blocked):", e);
  }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = useCallback((l) => {
    if (!SUPPORTED.includes(l)) return;
    setLangState(l);
    persistLang(l);
  }, []);

  const t = useCallback(
    (path) => {
      const val = lookup(translations[lang], path);
      if (val != null) return val;
      const fallback = lookup(translations.en, path);
      return fallback != null ? fallback : path;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useT = () => useContext(LanguageContext);
